if (typeof ProductTabs !== 'function') {
  class ProductTabs extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    // Live queries — <css-slider> clones/moves card wrappers after we mount,
    // so cached element references go stale. Re-query on every access.
    get cards() {
      return Array.from(this.querySelectorAll('.care-use__product-card-wrapper'));
    }

    get panels() {
      return Array.from(this.querySelectorAll('.care-use__product-panel'));
    }

    init() {
      const cards = this.cards;
      const panels = this.panels;
      if (cards.length === 0 || panels.length === 0) return;

      // Rewrite <a href> inside cards to a local hash so the status bar
      // shows e.g. "#chopsticks" instead of the product URL. Functional click
      // already prevented by our capture-phase handler, but the hover preview
      // would still leak the product link otherwise.
      cards.forEach((card) => {
        const handle = card.dataset.handle;
        if (!handle) return;
        card.querySelectorAll('a[href]').forEach((a) => {
          if (a.dataset.careHrefRewritten === '1') return;
          a.dataset.careHrefRewritten = '1';
          a.setAttribute('href', '#' + handle);
        });
      });

      // Avoid attaching multiple listeners across hot reloads.
      if (this._listenersAttached) return;
      this._listenersAttached = true;

      // Capture phase — fires before any <a> default navigation.
      this.addEventListener('click', (e) => {
        const wrapper = e.target.closest('.care-use__product-card-wrapper');
        if (!wrapper || !this.contains(wrapper)) return;
        const idx = this.cards.indexOf(wrapper);
        if (idx < 0) return;
        if (e.target.closest('a')) {
          e.preventDefault();
          e.stopPropagation();
        }
        this.activate(idx);
      }, true);

      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const wrapper = e.target.closest('.care-use__product-card-wrapper');
        if (!wrapper || !this.contains(wrapper)) return;
        const idx = this.cards.indexOf(wrapper);
        if (idx < 0) return;
        e.preventDefault();
        this.activate(idx);
      }, true);

      // <css-slider> arrows / dots / swipe change `slider.index` (the visible
      // group with groupCells=true) and dispatch 'change'. The component
      // itself does NOT scroll the holder — we have to translate the new
      // group index into a scroll position and apply it. We deliberately do
      // not change the active card here: the strip slides but the user picks
      // the active card by clicking.
      this.querySelectorAll('css-slider').forEach((slider) => {
        slider.addEventListener('change', () => {
          // The slider's index also moves while a finger is dragging the strip
          // (its IntersectionObserver reports cards crossing the viewport), and
          // scrolling in response would yank the strip out from under the
          // gesture. Leave the strip alone until the hand is off it.
          if (this.userIsScrolling()) return;
          // Same when activate() is the one moving the slider: changeSlide()
          // dispatches this event itself, and scrolling again here would just
          // duplicate the animation it has already started.
          if (this._selfDriving) return;
          const holder = slider.querySelector('.css-slider-holder');
          const items = slider.indexedItems;
          if (!holder || !items || items.length === 0) return;
          const idx = slider.index;
          const targetItem = items[idx];
          if (!targetItem) return;
          const left = targetItem.offsetLeft - items[0].offsetLeft;
          if (Math.abs(holder.scrollLeft - left) > 1) {
            holder.scrollTo({ left, behavior: 'smooth' });
          }
        });
      });

      this.bindSwipeSync();
      this.syncFromHash();
      window.addEventListener('hashchange', () => this.syncFromHash());
    }

    // Opt-in (data-swipe-selects="true"): whatever the swipe comes to rest on
    // becomes the active card, so the strip and the panel below never disagree.
    // Off by default — without it the strip slides and the user picks by tapping.
    //
    // Driven by the holder's scroll rather than the slider's own `change` event:
    // the slider derives its index from an IntersectionObserver against the
    // viewport, which reports nothing while the page sits in a background tab
    // and depends on the observer option staying on. Reading scrollLeft needs
    // neither, and covers drag-scrolling as well as a finger swipe.
    //
    // Settle-triggered, not live: a tap also scrolls the strip, and acting on
    // every frame of that smooth scroll would flick the content through every
    // card on the way.
    bindSwipeSync(attempt = 0) {
      if (this.dataset.swipeSelects !== 'true') return;
      const holders = this.querySelectorAll('css-slider .css-slider-holder');
      // The scroll container only exists once <css-slider> has built itself,
      // which happens after this component's constructor runs.
      if (holders.length === 0) {
        if (attempt < 40) setTimeout(() => this.bindSwipeSync(attempt + 1), 30);
        return;
      }
      holders.forEach((holder) => {
        if (holder.dataset.swipeSyncBound === '1') return;
        holder.dataset.swipeSyncBound = '1';

        // Hand-on-the-strip window, read by the 'change' handler above. The
        // tail after release covers momentum and the snap that follows it.
        const held = (on) => {
          this._pointerDown = on;
          if (!on) this._userScrollUntil = Date.now() + 400;
        };
        holder.addEventListener('pointerdown', () => held(true), { passive: true });
        holder.addEventListener('pointerup', () => held(false), { passive: true });
        holder.addEventListener('pointercancel', () => held(false), { passive: true });
        holder.addEventListener('touchstart', () => held(true), { passive: true });
        holder.addEventListener('touchend', () => held(false), { passive: true });

        let settle = null;
        holder.addEventListener('scroll', () => {
          clearTimeout(settle);
          settle = setTimeout(() => {
            // Not a swipe — activate() is moving the strip itself.
            if (Date.now() < (this._selfScrollUntil || 0)) return;
            const cards = this.cards;
            if (cards.length === 0) return;
            const base = cards[0].offsetLeft;
            let nearest = -1;
            let shortest = Infinity;
            cards.forEach((card, idx) => {
              const distance = Math.abs(card.offsetLeft - base - holder.scrollLeft);
              if (distance < shortest) {
                shortest = distance;
                nearest = idx;
              }
            });
            // Already-active guard: activate() can nudge the strip, which would
            // fire this again — the guard is what makes that converge.
            if (nearest >= 0 && !cards[nearest].classList.contains('active')) {
              this.activate(nearest, { scrollIntoView: false });
            }
          }, 120);
        }, { passive: true });
      });
    }

    userIsScrolling() {
      return this._pointerDown === true || Date.now() < (this._userScrollUntil || 0);
    }

    activate(i, { updateHash = true, scrollIntoView = true } = {}) {
      const cards = this.cards;
      const panels = this.panels;

      cards.forEach((c, idx) => {
        c.classList.toggle('active', idx === i);
      });
      panels.forEach((p, idx) => {
        const on = idx === i;
        p.classList.toggle('active', on);
        if (on) {
          p.removeAttribute('aria-hidden');
        } else {
          p.setAttribute('aria-hidden', 'true');
        }
      });

      if (updateHash) {
        const handle = cards[i]?.dataset.handle;
        if (handle) {
          history.replaceState(null, '', `#${handle}`);
        }
      }

      // Scroll the slider to show the active card ONLY when it is not already
      // visible. With few cards the entire row fits on screen, so clicking
      // a visible card should not move the row at all. With more cards (or on
      // mobile where the slider must paginate) we still need to bring an
      // off-screen card into view after slider arrow / dot navigation.
      const slider = this.querySelector('css-slider');
      const holder = slider?.querySelector('.css-slider-holder');
      const target = cards[i];
      // Skipped when the swipe itself chose this card — the strip is already
      // where the finger left it, and nudging it would fight the gesture.
      if (slider && holder && target && scrollIntoView) {
        const targetRect = target.getBoundingClientRect();
        const holderRect = holder.getBoundingClientRect();
        const fullyVisible =
          targetRect.left >= holderRect.left - 1 &&
          targetRect.right <= holderRect.right + 1;
        // `i` is the card index; the slider pages in groups (groupCells), so it
        // has to be given a PAGE index. Without the conversion, card 3 in a
        // 4-per-page slider would set index 2, past the page count (2 pages for
        // 6 cards), and the arrows would stop working — their guard
        // `index + 1 < length` never passes from an out-of-bounds value.
        const perPage = slider.slidesPerPage || 1;
        const pageIdx = Math.floor(i / perPage);
        if (!fullyVisible) {
          // Drive the slider through its own changeSlide() instead of scrolling
          // the holder by hand: it holds the slider's IntersectionObserver off
          // for the length of the animation. Scrolling directly leaves that
          // observer live, and it reports every card crossing half the viewport
          // on the way — each report fires 'change', whose handler scrolls to
          // that card's page, and the strip lands back where it started. That
          // is what made a deep link show the first page with the chosen card
          // still off to the right. changeSlide() also keeps index, arrows and
          // dots in step, which the bare scroll did not.
          this._selfDriving = true;
          // The animation runs on past this call, and its scroll events reach
          // the swipe-sync settle handler below, which would then treat our own
          // move as a swipe and select whatever card ended up leftmost. Hold it
          // off until the strip has come to rest.
          this._selfScrollUntil = Date.now() + 900;
          slider.changeSlide(pageIdx); // dispatches 'change' synchronously
          this._selfDriving = false;
        } else if (typeof slider.index === 'number' && slider.index !== pageIdx) {
          slider.index = pageIdx;
        }
      }
    }

    syncFromHash() {
      const handle = window.location.hash.replace(/^#/, '');
      if (!handle) return;
      const idx = this.cards.findIndex((c) => c.dataset.handle === handle);
      if (idx < 0) return;
      const slider = this.querySelector('css-slider');
      // A deep link can arrive before <css-slider> has finished building
      // itself, and until it has there is no strip to move and no page size to
      // aim at — the card would be selected but left off-screen behind the
      // fold. Show its panel straight away, then bring the card in once the
      // slider is up.
      if (slider && !slider.sliderEnabled) {
        this.activate(idx, { updateHash: false, scrollIntoView: false });
        slider.addEventListener('ready', () => this.activate(idx, { updateHash: false }), { once: true });
        return;
      }
      this.activate(idx, { updateHash: false });
    }
  }

  if (typeof customElements.get('product-tabs') === 'undefined') {
    customElements.define('product-tabs', ProductTabs);
  }

  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelector('product-tabs')?.init();
  });
}
